<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Price Rules</h1>
          <p class="lede">Auto-discount by day &amp; time — happy hour, weekday specials, category promos.</p>
        </div>
        <button class="primary" @click="openNew" v-if="canEdit">+ New rule</button>
      </div>

      <div class="card">
        <table v-if="rules.length">
          <thead>
            <tr>
              <th>Name</th><th>Scope</th><th class="r">Discount</th><th>Days</th><th>Time</th><th>Valid</th><th>Active</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rules" :key="r.id" :class="{ inactive: !r.is_active }">
              <td><strong>{{ r.name }}</strong></td>
              <td>
                <span v-if="r.scope === 'all'" class="muted small">All items</span>
                <span v-else-if="r.scope === 'category'">Category: <strong>{{ r.category_name || '?' }}</strong></span>
                <span v-else-if="r.scope === 'product'">Product: <strong>{{ r.product_name || '?' }}</strong></span>
              </td>
              <td class="r"><strong>{{ r.discount_type === 'percent' ? r.value + '% off' : 'KES ' + fmt(r.value) + ' off' }}</strong></td>
              <td>
                <div class="days-row">
                  <span v-for="d in weekdays" :key="d.key" :class="{'day-chip':1, on: isDayOn(r, d.key)}">{{ d.short }}</span>
                </div>
              </td>
              <td class="muted small">{{ r.time_from ? r.time_from.slice(0,5) + '–' + (r.time_to || '').slice(0,5) : 'all day' }}</td>
              <td class="muted small">{{ r.valid_from || '—' }} → {{ r.valid_to || '—' }}</td>
              <td>{{ r.is_active ? '✓' : '—' }}</td>
              <td>
                <button class="secondary small" @click="openEdit(r)" v-if="canEdit">Edit</button>
                <button class="secondary small" @click="toggle(r)" v-if="canEdit" style="margin-left:4px">{{ r.is_active ? 'Disable' : 'Enable' }}</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No price rules yet. Click <strong>+ New rule</strong> to add a happy hour, weekday special or category promo.</p>
      </div>
    </div>

    <div v-if="showForm" class="modal-back" @click.self="showForm=false">
      <div class="modal card wide">
        <h3>{{ form.id ? 'Edit rule' : 'New rule' }}</h3>
        <label>Rule name</label>
        <input v-model="form.name" placeholder="e.g. Happy hour · Wednesday special" />

        <label>Applies to</label>
        <div class="row-2">
          <select v-model="form.scope" @change="onScopeChange">
            <option value="all">All products &amp; services</option>
            <option value="category">A category</option>
            <option value="product">One product</option>
          </select>
          <select v-if="form.scope === 'category'" v-model.number="form.scopeId">
            <option :value="0" disabled>-- pick --</option>
            <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
          <select v-else-if="form.scope === 'product'" v-model.number="form.scopeId">
            <option :value="0" disabled>-- pick --</option>
            <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </div>

        <div class="row-2">
          <div>
            <label>Discount type</label>
            <select v-model="form.discountType">
              <option value="percent">% off</option>
              <option value="amount">KES amount off</option>
            </select>
          </div>
          <div>
            <label>{{ form.discountType === 'percent' ? 'Percent (0–99)' : 'Amount (KES)' }}</label>
            <input type="number" v-model.number="form.value" min="0" step="0.01" :max="form.discountType === 'percent' ? 99 : undefined" />
          </div>
        </div>

        <label>Days of the week (leave empty for every day)</label>
        <div class="day-picker">
          <button v-for="d in weekdays" :key="d.key"
                  :class="{'day-chip':1, on: form.days.includes(d.key)}"
                  @click.prevent="toggleDay(d.key)">{{ d.short }}</button>
        </div>

        <div class="row-2">
          <div><label>Time from (optional)</label><input type="time" v-model="form.timeFrom" /></div>
          <div><label>Time to (optional)</label><input type="time" v-model="form.timeTo" /></div>
        </div>
        <p class="muted small">Blank = all day. If time_to &lt; time_from, treated as an overnight window (e.g. 22:00 – 02:00).</p>

        <div class="row-2">
          <div><label>Valid from date (optional)</label><input type="date" v-model="form.validFrom" /></div>
          <div><label>Valid to date (optional)</label><input type="date" v-model="form.validTo" /></div>
        </div>

        <label>Priority (higher wins on conflict)</label>
        <input type="number" v-model.number="form.priority" min="0" />

        <div class="modal-actions">
          <button v-if="form.id && userRole === 'owner'" class="danger" @click="remove" style="margin-right:auto">Delete</button>
          <button class="secondary" @click="showForm=false">Cancel</button>
          <button class="primary" @click="save" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

const WEEKDAYS = [
  { key: 'mon', short: 'M' }, { key: 'tue', short: 'T' }, { key: 'wed', short: 'W' },
  { key: 'thu', short: 'T' }, { key: 'fri', short: 'F' }, { key: 'sat', short: 'S' },
  { key: 'sun', short: 'S' },
];

export default {
  name: 'PriceRules',
  components: { Navigation },
  data() {
    return {
      rules: [], categories: [], products: [],
      userRole: sessionStorage.getItem('userRole') || '',
      weekdays: WEEKDAYS,
      showForm: false, saving: false, err: '',
      form: { id: 0, name: '', scope: 'all', scopeId: 0, discountType: 'percent', value: 10, days: [], timeFrom: '', timeTo: '', validFrom: '', validTo: '', priority: 0 },
    };
  },
  computed: {
    canEdit() { return ['owner','manager'].includes(this.userRole); }
  },
  async mounted() {
    await Promise.all([this.load(), this.loadCategories(), this.loadProducts()]);
  },
  methods: {
    fmt(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    isDayOn(r, key) {
      if (!r.days_of_week) return true;   // empty = all days
      return r.days_of_week.split(',').map(s => s.trim().toLowerCase()).includes(key);
    },
    async load() { const { data } = await execute({ function: 'listPriceRules' }, paysol); if (data.success) this.rules = data.data; },
    async loadCategories() { const { data } = await execute({ function: 'listCategories' }, paysol); if (data.success) this.categories = data.data; },
    async loadProducts()  { const { data } = await execute({ function: 'listProducts' }, paysol); if (data.success) this.products = data.data; },
    onScopeChange() { this.form.scopeId = 0; },
    toggleDay(k) {
      const i = this.form.days.indexOf(k);
      if (i >= 0) this.form.days.splice(i, 1);
      else this.form.days.push(k);
    },
    openNew() {
      this.form = { id: 0, name: '', scope: 'all', scopeId: 0, discountType: 'percent', value: 10, days: [], timeFrom: '', timeTo: '', validFrom: '', validTo: '', priority: 0 };
      this.err = ''; this.showForm = true;
    },
    openEdit(r) {
      this.form = {
        id: r.id, name: r.name, scope: r.scope, scopeId: Number(r.scope_id || 0),
        discountType: r.discount_type, value: Number(r.value),
        days: r.days_of_week ? r.days_of_week.split(',').map(s => s.trim()) : [],
        timeFrom: r.time_from ? r.time_from.slice(0,5) : '',
        timeTo:   r.time_to   ? r.time_to.slice(0,5)   : '',
        validFrom: r.valid_from || '', validTo: r.valid_to || '',
        priority: Number(r.priority || 0),
      };
      this.err = ''; this.showForm = true;
    },
    async save() {
      this.saving = true; this.err = '';
      try {
        const payload = { ...this.form, daysOfWeek: this.form.days.join(','), function: 'savePriceRule' };
        const { data } = await execute(payload, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showForm = false; await this.load();
      } finally { this.saving = false; }
    },
    async toggle(r) {
      const { data } = await execute({ function: 'togglePriceRule', id: r.id }, paysol);
      if (data.success) await this.load();
    },
    async remove() {
      if (!confirm(`Delete rule "${this.form.name}"?`)) return;
      const { data } = await execute({ function: 'deletePriceRule', id: this.form.id }, paysol);
      if (data.success) { this.showForm = false; await this.load(); }
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1300px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; }
.r { text-align: right; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; padding: 40px 0; }
.inactive td { opacity: 0.55; }
button.small { padding: 4px 10px; font-size: 11px; }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }

.days-row { display: flex; gap: 2px; }
.day-chip {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 4px; font-size: 10px; font-weight: 700;
  background: var(--paysol-bg-elev); color: var(--paysol-muted); border: 1px solid var(--paysol-line);
  cursor: default;
}
.day-chip.on { background: var(--paysol-teal); color: #fff; border-color: var(--paysol-teal); }

.day-picker { display: flex; gap: 4px; margin: 4px 0 12px; }
.day-picker .day-chip { cursor: pointer; padding: 6px 12px; width: auto; height: auto; font-size: 12px; }
.day-picker .day-chip:hover:not(.on) { background: var(--paysol-teal-glow); color: var(--paysol-teal); }

.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 500px; max-height: 90vh; overflow-y: auto; }
.modal.wide { max-width: 620px; }
.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
</style>
